import React from 'react'
import cx from 'classnames'
import { TabBar, TabItem } from 'components/TabBar'
import { observer } from 'mobx-react'
import JobViewModel, { Tab } from '../JobViewModel'
import styles from './HeaderView.sass'
import { useHoverState } from 'hooks/hover-notify'
import Tooltip from 'components/Tooltip'
import {
  Dropdown,
  DropdownDivider,
  DropdownItem,
  DropdownItemTitle,
  DropdownScroll,
  DropdownTitle,
} from 'components/Dropdown'
import { CollaboratorRole } from 'jobs/dtos/job'
import { isChildrenEmpty } from 'utils/react-util'
import Button from 'components/Button'
import Icon from 'components/Icon'
import { SplitViewToggleButton } from 'components/SplitView'
import { formatToLongDateWithTimeZone } from 'utils/date-util'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  vm: JobViewModel
  onJobListSidebarToggleClick?: () => unknown
  onJobListHoverChange: (isHovering: boolean) => unknown
  isJobListSidebarVisible: boolean
}

const deadlineFormatter = new Intl.DateTimeFormat('en-US', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour12: true,
  hour: 'numeric',
  minute: 'numeric',
})

export default observer(function HeaderView({
  vm,
  onJobListSidebarToggleClick,
  onJobListHoverChange,
  isJobListSidebarVisible,
}: Props) {
  const makeTabProps = (
    tab: Tab,
    badgeCount?: number,
  ): {
    active?: boolean
    badgeCount?: number
    badgePosition?: 'top' | 'right'
    onClick?: () => unknown
  } => ({
    active: vm.currentTab === tab,
    onClick: () => vm.setTab(tab),
    badgeCount,
    badgePosition: 'right',
  })
  const toggleRef = React.useRef<HTMLAnchorElement | null>(null)
  const isToggleHovering = useHoverState(toggleRef, onJobListHoverChange)

  return (
    <div className={styles.root}>
      <SplitViewToggleButtonContainer
        onJobListSidebarToggleClick={onJobListSidebarToggleClick}
        isToggleHovering={isToggleHovering}
        isJobListSidebarVisible={isJobListSidebarVisible}
      />
      <div className={styles.jobInfo}>
        <JobHeaderInfo vm={vm} />
        <Actions vm={vm} />
      </div>
      {vm.job.status !== 'UNDER_CONSTRUCTION' && (
        <TabBar>
          <TabItem
            {...makeTabProps(Tab.Details)}
            dataTest="details-tab-item-new"
          >
            Details
          </TabItem>
          <TabItem
            {...makeTabProps(Tab.Communication, vm.conversationVM.unreadCount)}
            dataTest="communication-tab-item-new"
          >
            Chat
          </TabItem>
        </TabBar>
      )}
    </div>
  )
})

interface JobHeaderInfoProps {
  vm: JobViewModel
}

const JobHeaderInfo = observer(({ vm }: JobHeaderInfoProps) => {
  return (
    <div>
      <h3 className={styles.jobName}>{vm.job.displayName}</h3>
      <div>
        <span className={styles.statusLabel}>Status</span>
        <span
          className={cx(
            styles.statusIndicator,
            vm.job.isDraft && styles.onboarding,
          )}
        ></span>
        <span className={styles.status} data-test="job-header-status">
          {vm.job.formattedStatus}
        </span>
        {vm.job.dateDeadline && (
          <>
            <Divider />
            <span className={styles.statusLabel}>Due date</span>
            <Tooltip
              placement="bottom"
              content={deadlineFormatter.format(vm.job.dateDeadline)}
            >
              <span className={styles.daysToComplete}>{vm.daysLeft}</span>
            </Tooltip>
          </>
        )}

        <PayoutSituation vm={vm} />
        <StatusHistorySummary vm={vm} />
        <ConsultationSummary vm={vm} />
        <NotesSituation vm={vm} />
      </div>
    </div>
  )
})

/**
 * Payout Situation
 */
export const PayoutSituation = observer(function PayoutSituation({
  vm,
}: {
  vm: JobViewModel
}) {
  if (!vm.canViewPayoutSplit) {
    return null
  }

  switch (vm.job.payoutSplit?.situation.type) {
    case 'Manual':
      return (
        <>
          <Divider />
          <span className={styles.statusLabel}>Split payout</span>
          <span className={styles.splitPayoutManuallyAllocated}>Allocated</span>
        </>
      )
    case 'Auto':
      return (
        <>
          <Divider />
          <span className={styles.statusLabel}>Split payout</span>
          <span className={styles.splitPayoutAutoAdjusted}>Automated</span>
        </>
      )
    default:
      return null
  }
})

export const NotesSituation = observer(function NotesSituation({
  vm,
}: {
  vm: JobViewModel
}) {
  if (!vm.notesVM.areNotesEnabled) {
    return null
  }

  return (
    <>
      <Divider />
      <span className={styles.notesTag}>Limited communication</span>
    </>
  )
})

/**
 * Job time stats depending on the status of the Job.
 */
export const StatusHistorySummary = observer(function StatusHistorySummary({
  vm,
}: {
  vm: JobViewModel
}) {
  const statusHistorySummary = vm.statusHistorySummary
  if (statusHistorySummary === null) {
    return null
  }

  return (
    <>
      <Divider />
      {statusHistorySummary.prefix !== null && (
        <span className={styles.statusHistorySummaryPrefix}>
          {statusHistorySummary.prefix}
        </span>
      )}
      <span className={styles.statusHistorySummaryTime}>
        {statusHistorySummary.time}
      </span>
      {statusHistorySummary.suffix !== null && (
        <span className={styles.statusHistorySummarySuffix}>
          {statusHistorySummary.suffix}
        </span>
      )}
    </>
  )
})

/**
 * Displays the booked time of the consultation if available.
 */
export const ConsultationSummary = observer(function ConsultationSummary({
  vm,
}: {
  vm: JobViewModel
}) {
  const consultation = vm.job.consultation

  if (!consultation || consultation?.skipScheduling) {
    return null
  }

  if (vm.consultationIsPendingScheduling) {
    return (
      <>
        <Divider />
        <span className={styles.statusLabel}>
          {vm.consultationPendingSchedulingText}
        </span>
      </>
    )
  }

  const bookedStartTime = consultation.bookedTime?.startTime
  if (!bookedStartTime) {
    return null
  }

  return (
    <>
      <Divider />
      <span className={styles.statusLabel}>{'Call scheduled '}</span>
      <span className={styles.callTime}>
        {formatToLongDateWithTimeZone(bookedStartTime)}
      </span>
    </>
  )
})

/**
 * Divider
 */
const Divider = () => <span className={styles.divider}>|</span>

interface ActionsProps {
  vm: JobViewModel
}

export const Actions = observer(function Actions({ vm }: ActionsProps) {
  const currentUserRole: CollaboratorRole =
    vm.currentUserCollaborator?.role || 'SUPPORTING'

  const dropdownItems = (
    <>
      {vm.canAddCouponAsAdmin && (
        <DropdownItem
          onClick={vm.applyCouponAsAdminVM.show}
          data-test="add-coupon-dropdown-item"
        >
          <DropdownItemTitle>Add coupon</DropdownItemTitle>
        </DropdownItem>
      )}

      {vm.canAddManualInvoice && (
        <DropdownItem
          onClick={vm.addManualInvoiceDialogVM.show}
          data-test="add-manual-invoice-dropdown-item"
        >
          <DropdownItemTitle>Add manual invoice</DropdownItemTitle>
        </DropdownItem>
      )}

      {vm.canViewAmendments && (
        <DropdownItem
          onClick={vm.createAmendmentVM.show}
          data-test="add-new-amendment-dropdown-item"
        >
          <DropdownItemTitle>Add new amendment</DropdownItemTitle>
        </DropdownItem>
      )}

      {vm.canAddCollaboratorsAsAdmin && (
        <DropdownItem
          onClick={vm.addCollaboratorsAsAdminVM.show}
          data-test="add-pro-dropdown-item"
        >
          <DropdownItemTitle>Add collaborator</DropdownItemTitle>
        </DropdownItem>
      )}

      {vm.canChargeOrRefundJob && (
        <DropdownItem
          onClick={vm.chargeOrRefundDialogVM.show}
          data-test="charge-or-refund-job-dropdown-item"
        >
          <DropdownItemTitle>Charge/refund job</DropdownItemTitle>
        </DropdownItem>
      )}

      {vm.canCorrectBookkeepingCycles && (
        <DropdownItem
          onClick={vm.bookkeepingSelectCycleDialogVM.show}
          data-test="correct-cycle-dropdown-item"
        >
          <DropdownItemTitle>Correct cycle</DropdownItemTitle>
        </DropdownItem>
      )}

      {vm.canCopy && (
        <DropdownItem onClick={vm.copyVM.show} data-test="copy-dropdown-item">
          <DropdownItemTitle>Clone job</DropdownItemTitle>
        </DropdownItem>
      )}

      {vm.canCloseAsAdmin && (
        <DropdownItem
          data-test="complete-job-as-admin-item"
          onClick={vm.requestCloseAsAdmin}
        >
          <DropdownItemTitle>Complete job as admin</DropdownItemTitle>
        </DropdownItem>
      )}

      {(vm.canDeleteJobAsAdmin || vm.canDeleteJobAsCollaborator) && (
        <DropdownItem
          onClick={vm.deleteJobDialogVM.show}
          data-test="delete-job-dropdown-item"
        >
          <DropdownItemTitle>Delete job</DropdownItemTitle>
        </DropdownItem>
      )}

      {vm.canDownloadBinder && (
        <DropdownItem onClick={() => vm.downloadBinder()}>
          <DropdownItemTitle>Download PBFX</DropdownItemTitle>
        </DropdownItem>
      )}

      {vm.showDraftView && (
        <DropdownItem
          onClick={vm.editDraft}
          data-test="edit-responses-dropdown-item"
        >
          <DropdownItemTitle>Edit responses</DropdownItemTitle>
        </DropdownItem>
      )}

      {vm.canMakeCorrections && !vm.showDraftView && (
        <DropdownItem
          onClick={vm.correctionsVM.show}
          data-test="make-corrections-dropdown-item"
        >
          <DropdownItemTitle>Make corrections</DropdownItemTitle>
        </DropdownItem>
      )}

      {vm.canNotifyPros && (
        <DropdownItem
          onClick={vm.notifyProsOfOpenJobDialogVM.show}
          data-test="notify-pros-dropdown-item"
        >
          <DropdownItemTitle>Notify pros</DropdownItemTitle>
        </DropdownItem>
      )}

      {vm.isPro && (
        <DropdownItem
          onClick={() =>
            window.open('https://www.taxfyle.com/pro-support-ticket')
          }
          data-test="job-details-pro-support-dropdown-item"
        >
          <DropdownItemTitle>Pro support</DropdownItemTitle>
        </DropdownItem>
      )}

      {vm.ratingsVM.canRatePro && (
        <DropdownItem
          onClick={vm.ratingsVM.showRateProDialog}
          data-test="rate-pro-dropdown-item"
        >
          <DropdownItemTitle>Rate Pro</DropdownItemTitle>
        </DropdownItem>
      )}

      {vm.canRemoveClientTeamAsAdmin && (
        <DropdownItem
          onClick={vm.removeClientTeamVM.show}
          data-test="remove-client-team-dropdown-item"
        >
          <DropdownItemTitle>Remove client team</DropdownItemTitle>
        </DropdownItem>
      )}

      {vm.canRemoveProsAsAdmin && (
        <DropdownItem
          onClick={vm.removeCollaboratorsAsAdminVM.show}
          data-test="remove-pro-dropdown-item"
        >
          <DropdownItemTitle>Remove collaborator</DropdownItemTitle>
        </DropdownItem>
      )}

      {vm.canRename && (
        <DropdownItem onClick={vm.renameVM.show}>
          <DropdownItemTitle>Rename job</DropdownItemTitle>
        </DropdownItem>
      )}

      {vm.canReopenAsAdmin && (
        <DropdownItem
          onClick={vm.reopenJobAsAdminVM.show}
          data-test="reopen-job-dropdown-item"
        >
          <DropdownItemTitle>Reopen job</DropdownItemTitle>
        </DropdownItem>
      )}

      {vm.canRequestScopeChanges && (
        <DropdownItem
          onClick={vm.requestScopeChangesVM.show}
          data-test="request-scope-changes-dropdown-item"
        >
          <DropdownItemTitle>Request scope changes</DropdownItemTitle>
        </DropdownItem>
      )}

      {vm.canSplitPayout && (
        <DropdownItem
          onClick={vm.splitPayoutDialogVM.show}
          data-test="split-payout-dropdown-item"
        >
          <DropdownItemTitle>Split payout</DropdownItemTitle>
        </DropdownItem>
      )}

      {vm.canTransferJobAsAdmin && (
        <DropdownItem
          onClick={() => vm.transferJobVM.open([vm.job])}
          data-test="transfer-job-dropdown-item"
        >
          <DropdownItemTitle>Transfer job</DropdownItemTitle>
        </DropdownItem>
      )}

      {vm.canUpdateDeadline && (
        <DropdownItem
          onClick={vm.updateDeadlineDialogVM.show}
          data-test="update-deadline-dropdown-item"
        >
          <DropdownItemTitle>Update deadline</DropdownItemTitle>
        </DropdownItem>
      )}

      {vm.canOverrideJobProgressionStep && (
        <DropdownItem
          onClick={vm.updateProgressionStepDialogVM.show}
          data-test="update-job-progression-dropdown-item"
        >
          <DropdownItemTitle>Update job progression</DropdownItemTitle>
        </DropdownItem>
      )}

      {vm.canUpdateJobScope && (
        <DropdownItem
          onClick={vm.updateJobScopeVM.show}
          data-test="update-job-scope-dropdown-item"
        >
          <DropdownItemTitle>Update job scope</DropdownItemTitle>
        </DropdownItem>
      )}

      {(vm.canForfeit || vm.canResumeJob || vm.canPutJobOnHold) && (
        <>
          <DropdownDivider />
          <DropdownTitle>Set job status</DropdownTitle>
          {vm.canForfeit && (
            <DropdownItem onClick={vm.forfeitVM.show}>
              <DropdownItemTitle>
                {currentUserRole === 'CHAMPION' ? <>Forfeit job</> : <>Leave</>}
              </DropdownItemTitle>
            </DropdownItem>
          )}

          {vm.canPutJobOnHold && (
            <DropdownItem onClick={() => vm.placeJobOnHold()}>
              <DropdownItemTitle>Place job on hold</DropdownItemTitle>
            </DropdownItem>
          )}

          {vm.canResumeJob && (
            <DropdownItem onClick={() => vm.resumeJob()}>
              <DropdownItemTitle>Resume job</DropdownItemTitle>
            </DropdownItem>
          )}
        </>
      )}

      {vm.showDraftView && vm.questionsCompleted && (
        <>
          <DropdownDivider />
          <DropdownTitle>Status</DropdownTitle>
          <DropdownItem
            onClick={vm.submitJobDialogVM.show}
            data-test="submit-job-dropdown-item"
          >
            <DropdownItemTitle>Submit job</DropdownItemTitle>
          </DropdownItem>
        </>
      )}
    </>
  )

  if (isChildrenEmpty(dropdownItems)) {
    return null
  }

  return (
    <div className={styles.actions}>
      <div className={styles.action}>
        <Dropdown
          autoDismiss
          width="small"
          position="bottom-end"
          button={
            <Button iconRight={<Icon auto name="dropdown" />}>Actions</Button>
          }
          dataTest="job-actions-dropdown"
          noBottomPadding
        >
          <DropdownTitle>Actions</DropdownTitle>
          <DropdownScroll autoHeightSize>{dropdownItems}</DropdownScroll>
        </Dropdown>
      </div>
    </div>
  )
})

interface SplitViewToggleButtonContainerProps {
  onJobListSidebarToggleClick?: () => unknown
  isToggleHovering: boolean
  isJobListSidebarVisible: boolean
  toggleRef?: React.Ref<HTMLAnchorElement>
}

export const SplitViewToggleButtonContainer = observer(
  ({
    onJobListSidebarToggleClick,
    isToggleHovering,
    isJobListSidebarVisible,
    toggleRef,
  }: SplitViewToggleButtonContainerProps) => {
    if (onJobListSidebarToggleClick) {
      return (
        <SplitViewToggleButton
          onClick={onJobListSidebarToggleClick}
          ref={toggleRef}
        >
          {isToggleHovering ? (
            isJobListSidebarVisible ? (
              <Icon name="hover-arrow-highlight" />
            ) : (
              <Icon name="hover-menu-highlight" />
            )
          ) : isJobListSidebarVisible ? (
            <Icon name="hover-arrow" />
          ) : (
            <Icon name="hover-menu" />
          )}
        </SplitViewToggleButton>
      )
    }
    return null
  },
)
